import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Backpack, Bed, BriefcaseBusiness, Bus, ChevronRight,
  Check, Clock, FileText, Flag, Languages, Mail, Map, MapPin, MessageSquareText,
  PhoneCall, Route, Search, ShieldAlert, Star, Ticket,
  Send, UserPlus, Users, Utensils, Wallet, X,
} from "lucide-react";
import { toast } from "sonner";
import { Btn, inputCls } from "@/components/Primitives";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import ParticipantMediaImage from "@/components/participant/ParticipantMediaImage";
import { api, getAllPages, formatApiError, formatApiErrorDetail } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import ParticipantsTab from "@/components/trip/ParticipantsTab";
import FinanceTab from "@/components/trip/FinanceTab";
import BookingsTab from "@/components/trip/BookingsTab";
import EnquiriesTab from "@/components/trip/EnquiriesTab";
import CommunicationsTab from "@/components/trip/CommunicationsTab";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { departureDays } from "@/lib/departures";
import { effectiveTripStaffing } from "@/lib/tripStaffing";

const RUN_EASE = [0.22, 1, 0.36, 1];
const ALL_BATCHES_ID = "__all__";

function motionEnabled(shouldReduceMotion, props) {
  return shouldReduceMotion ? {} : props;
}

const pageMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const RUN_TABS = [
  { id: "itinerary", label: "Itinerary", icon: Flag },
  { id: "bookings", label: "Bookings", icon: Ticket },
  { id: "participants", label: "Participants", icon: Users },
  { id: "budget", label: "Budget", icon: Wallet },
  { id: "messages", label: "Messages", icon: MessageSquareText },
  { id: "enquiries", label: "Enquiries", icon: Ticket },
];

const sectionMotion = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.48, ease: RUN_EASE } },
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function joinText(values, fallback = "Not set") {
  const text = asArray(values).filter(Boolean).join(", ");
  return text || fallback;
}

function compactDate(value) {
  if (!value) return "";
  return String(value).replace("T", " ").slice(0, 16);
}

function durationLabel(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

function uniqueById(items) {
  const seen = new Set();
  return asArray(items).filter((item) => {
    const id = item?.id || `${item?.vendor?.id || ""}-${item?.vendor?.name || ""}-${item?.assignment_type || ""}`;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function countLabel(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function runBatchTitle(batch, index) {
  return batch?.label || (index === 0 ? "Default batch" : `Batch ${index + 1}`);
}

function runBatchDates(batch) {
  return [batch?.start_date, batch?.end_date].filter(Boolean).join(" - ") || "Dates pending";
}

function runBookingBatchId(booking) {
  return booking?.batch_id || booking?.batch?.id || "";
}

function isActiveRunBooking(booking) {
  return !["cancelled", "refunded"].includes(booking?.state) && !["cancelled", "refunded"].includes(booking?.payment_status);
}

function runBatchStats(availability, bookings, selectedBatchId) {
  const batches = asArray(availability?.batches);
  const selectedBatch = selectedBatchId === ALL_BATCHES_ID ? null : batches.find((batch) => (batch.id || "") === selectedBatchId);
  const activeRows = asArray(bookings).filter((booking) => (
    isActiveRunBooking(booking) && (!selectedBatch?.id || runBookingBatchId(booking) === selectedBatch.id)
  ));
  const bookedPax = selectedBatch
    ? (Number.isFinite(Number(selectedBatch.booked)) ? Number(selectedBatch.booked) : activeRows.reduce((sum, booking) => sum + Number(booking.pax || 0), 0))
    : Number(availability?.booked || activeRows.reduce((sum, booking) => sum + Number(booking.pax || 0), 0));
  const capacity = selectedBatch ? Number(selectedBatch.capacity || 0) : Number(availability?.capacity || 0);
  const amountPaidMinor = activeRows.reduce((sum, booking) => sum + Number(booking.amount_paid_minor || 0), 0);
  const pendingDuesMinor = activeRows.reduce((sum, booking) => sum + Number(booking.amount_due_minor || 0), 0);

  return {
    selectedBatch,
    bookedPax,
    capacity,
    amountPaidMinor,
    pendingDuesMinor,
  };
}

function memberName(member) {
  return member?.full_name || member?.display_name || member?.name || member?.email || "Team member";
}

function memberRoles(member) {
  return asArray(member?.roles).filter(Boolean).map((role) => String(role).replace(/_/g, " "));
}

function vendorName(item) {
  return item?.vendor?.name || item?.provider || item?.property_name || item?.type || "Vendor";
}

function vendorMeta(item) {
  const vendor = item?.vendor || item || {};
  return [
    vendor.region || item?.region,
    vendor.address || item?.address,
    asArray(vendor.service_types).join(", "),
    item?.status,
  ].filter(Boolean).join(" - ");
}

function vendorResourceName(item) {
  return item?.vendor?.name || item?.provider || item?.property_name || item?.type || item?.assignment_type || "Vendor";
}

function documentResourceName(document) {
  return document?.name || String(document?.type || "Document").replace(/_/g, " ");
}

function logisticsPhone(item) {
  const contact = item?.contact_details || {};
  return (
    item?.vendor?.contact_phone ||
    item?.contact_phone ||
    contact.primary ||
    contact.phone ||
    contact.mobile ||
    item?.phone ||
    ""
  );
}

function phoneHref(value) {
  const cleaned = String(value || "").replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : "";
}

function DetailLine({ icon: Icon, children }) {
  const shouldReduceMotion = useReducedMotion();
  if (!children) return null;
  return (
    <motion.p
      {...motionEnabled(shouldReduceMotion, {
        whileHover: { x: 2 },
        transition: { duration: 0.18, ease: RUN_EASE },
      })}
      className="group flex min-w-0 items-start gap-1.5 text-xs leading-5 text-slate-500"
    >
      <Icon className="tp-run-detail-icon mt-0.5 h-3.5 w-3.5 shrink-0 text-[#2D7A65]" />
      <span className="min-w-0 break-words">{children}</span>
    </motion.p>
  );
}

function CallButton({ phone, name }) {
  const href = phoneHref(phone);
  const shouldReduceMotion = useReducedMotion();
  if (!href) return null;
  return (
    <motion.a
      href={href}
      {...motionEnabled(shouldReduceMotion, {
        whileHover: { y: -2, scale: 1.05 },
        whileTap: { scale: 0.95 },
      })}
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#E2DFD7] bg-white text-[#2D7A65] shadow-sm transition hover:bg-[#F7F1E5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] focus-visible:ring-offset-2"
      aria-label={`Call ${name || "contact"}`}
      title={`Call ${phone}`}
    >
      <PhoneCall className="h-4 w-4" />
    </motion.a>
  );
}

function RoleBadge({ role }) {
  const shouldReduceMotion = useReducedMotion();
  const label = String(role || "").replace(/_/g, " ");
  return (
    <motion.span
      {...motionEnabled(shouldReduceMotion, {
        whileHover: { y: -1, scale: 1.03 },
        whileTap: { scale: 0.98 },
      })}
      className="tp-run-chip rounded-full bg-[#F7F1E5] px-2 py-1 text-[11px] font-bold capitalize leading-none text-[#516259] ring-1 ring-[#E2DFD7]"
    >
      {label}
    </motion.span>
  );
}

function RatingStars({ value }) {
  const shouldReduceMotion = useReducedMotion();
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating <= 0) return null;
  const rounded = Math.round(Math.max(0, Math.min(5, rating)));
  return (
    <motion.div
      {...motionEnabled(shouldReduceMotion, {
        initial: { opacity: 0, x: -4 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.28, ease: RUN_EASE },
      })}
      className="mt-1 flex items-center gap-1 text-xs font-black text-[#7A5D2D]"
      aria-label={`Rating ${rating} out of 5`}
    >
      <span className="flex text-[#E5B85C]">
        {[0, 1, 2, 3, 4].map((index) => (
          <Star
            key={index}
            className={`tp-run-star h-3.5 w-3.5 ${index < rounded ? "fill-current" : ""}`}
            style={{ animationDelay: `${index * 0.16}s` }}
          />
        ))}
      </span>
      <span>{rating}/5</span>
    </motion.div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  const displayValue = value === 0 || value ? value : "Not set";
  const shouldReduceMotion = useReducedMotion();
  return (
    <motion.div
      {...motionEnabled(shouldReduceMotion, {
        initial: { opacity: 0, y: 10, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        whileHover: { y: -3, boxShadow: "0 16px 34px rgba(23, 33, 29, 0.12)" },
        transition: { duration: 0.35, ease: RUN_EASE },
      })}
      className="tp-run-mini-stat rounded-xl border border-[#E2DFD7] bg-[#FFFCF7]/85 px-3 py-2.5"
    >
      <p className="flex items-center gap-2 text-[11px] font-black uppercase text-[#516259]">
        <Icon className="h-4 w-4 text-[#2D7A65]" /> {label}
      </p>
      <p className="mt-1 text-base font-black text-[#17211D]">{displayValue}</p>
    </motion.div>
  );
}

function SectionHeader({ icon: Icon, title, action }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-sm font-black text-[#17211D]">
        <Icon className="tp-run-section-icon h-4 w-4 text-[#2D7A65]" /> {title}
      </h3>
      {action}
    </div>
  );
}

function StatusPill({ children, tone = "slate" }) {
  const shouldReduceMotion = useReducedMotion();
  const tones = {
    slate: "bg-slate-100 text-slate-600 ring-slate-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
    blue: "bg-sky-50 text-sky-700 ring-sky-200",
    purple: "bg-violet-50 text-violet-700 ring-violet-200",
  };
  const pulseClass = ["green", "amber", "rose"].includes(tone) ? "tp-run-status-pulse" : "";
  return (
    <motion.span
      {...motionEnabled(shouldReduceMotion, {
        initial: { opacity: 0, scale: 0.92 },
        animate: { opacity: 1, scale: 1 },
        whileHover: { y: -1, scale: 1.03 },
        transition: { duration: 0.2, ease: RUN_EASE },
      })}
      className={`tp-run-status-pill inline-flex rounded-full px-2 py-0.5 text-[11px] font-black capitalize ring-1 ${pulseClass} ${tones[tone] || tones.slate}`}
    >
      {String(children || "").replace(/_/g, " ")}
    </motion.span>
  );
}

function paymentTone(status) {
  if (status === "paid" || status === "deposit_paid") return "green";
  if (status === "cancelled" || status === "refunded") return "rose";
  if (status === "unpaid") return "rose";
  if (status === "partially_paid") return "amber";
  if (status && status !== "none") return "blue";
  return "slate";
}

function inviteLabel(person) {
  return person.kind === "invite" ? "Invited" : "Joined";
}

function inviteTone(person) {
  return person.kind === "invite" ? "amber" : "green";
}

function paymentLabel(status) {
  if (status === "paid") return "Paid";
  if (status === "deposit_paid") return "Deposit paid";
  if (status === "partially_paid") return "Part paid";
  if (status === "cancelled") return "Cancelled";
  if (status === "refunded") return "Refunded";
  if (status === "none") return "No payment";
  return "Unpaid";
}

function participantPhone(person) {
  return person?.profile?.contact?.phone || person?.phone || "";
}

function participantEmail(person) {
  return person?.email || person?.profile?.contact?.email || "";
}

function participantInitials(person) {
  const source = person?.display_name || person?.email || "Traveller";
  const parts = String(source).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(source).slice(0, 2).toUpperCase();
}

function participantDocs(person, documents) {
  if (!person?.id) return [];
  return asArray(documents).filter((doc) => doc.owner_type === "participant" && doc.owner_ref === person.id);
}

function normalizeDocKey(value) {
  return String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

function tripParticipantDocumentRequirements(trip) {
  return asArray(trip?.participant_document_requirements)
    .filter((requirement) => requirement?.name || requirement?.type)
    .slice()
    .sort((a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0));
}

function documentHasUpload(doc) {
  if (!doc) return false;
  if (["uploaded", "under_review", "approved"].includes(doc.status)) return true;
  return Boolean(doc.link || doc.file_url || doc.current_version_id || doc.signed_at);
}

function documentMatchesRequirement(doc, requirement, sameTypeCount) {
  if (!doc || !requirement) return false;
  const requirementId = requirement.id;
  if (requirementId && [doc.requirement_id, doc.participant_document_requirement_id].includes(requirementId)) return true;
  const docType = normalizeDocKey(doc.type);
  const requirementType = normalizeDocKey(requirement.type);
  if (docType && requirementType && docType !== requirementType) return false;
  const docName = normalizeDocKey(doc.name);
  const requirementName = normalizeDocKey(requirement.name);
  if (docName && requirementName && (docName === requirementName || docName.includes(requirementName) || requirementName.includes(docName))) return true;
  return sameTypeCount === 1 && docType && requirementType && docType === requirementType;
}

function participantDocumentChecklist(person, documents, trip) {
  const requirements = tripParticipantDocumentRequirements(trip);
  const docs = participantDocs(person, documents);
  const used = new Set();
  return requirements.map((requirement) => {
    const sameTypeCount = requirements.filter((item) => normalizeDocKey(item.type) === normalizeDocKey(requirement.type)).length;
    const doc = docs.find((candidate) => {
      if (used.has(candidate.id)) return false;
      return documentMatchesRequirement(candidate, requirement, sameTypeCount);
    });
    if (doc?.id) used.add(doc.id);
    const uploaded = documentHasUpload(doc);
    return {
      requirement,
      doc,
      uploaded,
      label: requirement.name || requirement.type || "Document",
      value: uploaded ? (doc.name || "Uploaded") : (doc?.status === "requested" ? "Requested" : "Missing"),
      required: requirement.required !== false,
    };
  });
}

function participantDocSummary(person, documents, trip) {
  const checklist = participantDocumentChecklist(person, documents, trip);
  if (checklist.length) {
    const requiredItems = checklist.filter((item) => item.required);
    const requiredTotal = requiredItems.length || checklist.length;
    const requiredUploaded = (requiredItems.length ? requiredItems : checklist).filter((item) => item.uploaded).length;
    if (requiredUploaded === requiredTotal) return { label: "Complete", tone: "green", count: requiredUploaded, total: requiredTotal };
    if (requiredUploaded > 0) return { label: `${requiredUploaded}/${requiredTotal}`, tone: "amber", count: requiredUploaded, total: requiredTotal };
    return { label: `0/${requiredTotal}`, tone: "rose", count: 0, total: requiredTotal };
  }
  const docs = participantDocs(person, documents);
  if (!docs.length) return { label: "No docs", tone: "slate", count: 0 };
  if (docs.some((doc) => ["requested", "rejected"].includes(doc.status))) {
    return { label: "Needs action", tone: "rose", count: docs.length };
  }
  if (docs.some((doc) => ["uploaded", "under_review"].includes(doc.status))) {
    return { label: "Review", tone: "amber", count: docs.length };
  }
  return { label: "Complete", tone: "green", count: docs.length };
}

function participantLocationShare(person, locationShares) {
  if (!person?.id) return null;
  return asArray(locationShares).find((share) => share.participant_id === person.id);
}

function participantNeedsAction(person, documents, trip) {
  if (person.kind === "invite") return true;
  const paymentStatus = person.payment_status || "none";
  const due = Number(person.amount_due_minor || 0);
  const docs = participantDocSummary(person, documents, trip);
  return due > 0 || ["unpaid", "partially_paid", "none"].includes(paymentStatus) || ["rose", "amber"].includes(docs.tone);
}

function checkInLabel(person) {
  const status = person?.last_checkin_status;
  const at = compactDate(person?.last_checkin_at);
  if (!status && !at) return "No check-in";
  return [status ? String(status).replace(/_/g, " ") : "", at].filter(Boolean).join(" - ");
}

function checkInTone(status) {
  if (status === "present") return "green";
  if (status === "late") return "amber";
  if (status === "absent" || status === "needs_attention") return "rose";
  return "slate";
}

function CheckInStatus({ person }) {
  return <StatusPill tone={checkInTone(person?.last_checkin_status)}>{checkInLabel(person)}</StatusPill>;
}

function ParticipantDetailLine({ label, value, children }) {
  if (!value && !children) return null;
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2 text-sm">
      <span className="text-[#516259]">{label}</span>
      <span className="min-w-0 break-words font-semibold text-[#17211D]">{children || value}</span>
    </div>
  );
}

function ParticipantDetailSection({ icon: Icon, title, children }) {
  return (
    <section className="border-t border-[#E2DFD7] py-3 first:border-t-0 first:pt-0 last:pb-0">
      <h4 className="mb-2 flex items-center gap-2 text-sm font-black text-[#17211D]">
        <Icon className="h-4 w-4 text-[#2D7A65]" /> {title}
      </h4>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function ManagementModal({ title, subtitle, onClose, children }) {
  const shouldReduceMotion = useReducedMotion();
  return createPortal(
    <motion.div
      {...motionEnabled(shouldReduceMotion, {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.18 },
      })}
      className="fixed inset-0 z-[1000] bg-[#07120F]/70 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <motion.div
        {...motionEnabled(shouldReduceMotion, {
          initial: { opacity: 0, y: 22, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 12, scale: 0.99 },
          transition: { duration: 0.28, ease: RUN_EASE },
        })}
        className="mx-auto flex max-h-full max-w-6xl flex-col overflow-hidden rounded-[1.75rem] bg-[#F3EDDF] shadow-2xl"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#D8D2C4] bg-[#17211D] px-4 py-3 text-white sm:px-5">
          <div>
            <p className="text-sm font-black">{title}</p>
            {subtitle && <p className="mt-1 text-xs text-white/70">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/20 bg-white/10 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C]"
            aria-label={`Close ${title}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-4 sm:p-5">{children}</div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

function RunParticipantsTab({
  trip,
  canParticipantView,
  canBookings,
  canDocs,
  participantSummary,
  documents,
  documentsError,
  locationShares,
  locationSharesError,
  onOpenParticipants,
  onOpenMessages,
  onRefresh,
}) {
  const shouldReduceMotion = useReducedMotion();
  const [participantQuery, setParticipantQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  const [docRequest, setDocRequest] = useState({ open: false, name: "", type: "government_id", busy: false });
  const people = useMemo(() => {
    const roster = participantSummary?.recent || [];
    const pendingInvites = participantSummary?.pendingInvites || [];
    return [
      ...roster.map((participant) => ({ kind: "participant", ...participant })),
      ...pendingInvites.map((invite) => ({ kind: "invite", ...invite })),
    ];
  }, [participantSummary]);
  const query = participantQuery.trim().toLowerCase();
  const searchedPeople = query ? people.filter((person) => {
    const paymentStatus = person.payment_status || "none";
    const docs = participantDocSummary(person, documents, trip);
    return [
      person.display_name,
      person.email,
      participantPhone(person),
      inviteLabel(person),
      paymentLabel(paymentStatus),
      docs.label,
      checkInLabel(person),
      person.amount_due_minor != null ? inr(person.amount_due_minor) : "",
    ].filter(Boolean).join(" ").toLowerCase().includes(query);
  }) : people;
  const filteredPeople = searchedPeople.filter((person) => {
    if (statusFilter === "needs_action") return participantNeedsAction(person, documents, trip);
    if (statusFilter === "live") return Boolean(participantLocationShare(person, locationShares));
    return true;
  });
  const selectedPerson = filteredPeople.find((person) => person.id === selectedId) || filteredPeople[0] || people[0] || null;
  const activeLocationShare = participantLocationShare(selectedPerson, locationShares);
  const selectedDocs = participantDocs(selectedPerson, documents);
  const selectedDocChecklist = participantDocumentChecklist(selectedPerson, documents, trip);
  const selectedDocSummary = participantDocSummary(selectedPerson, documents, trip);
  const emergencyContact = selectedPerson?.profile?.emergency_contacts?.[0];
  const allocation = selectedPerson?.allocation || {};
  const filters = [
    { id: "all", label: "All" },
    { id: "needs_action", label: "Needs action" },
    { id: "live", label: "Live" },
  ];
  const documentTypes = [
    ["government_id", "Government ID"],
    ["passport", "Passport"],
    ["visa", "Visa"],
    ["medical_fitness_certificate", "Medical fitness certificate"],
    ["health_declaration", "Health declaration"],
    ["liability_waiver", "Liability waiver"],
    ["guardian_consent", "Guardian consent"],
    ["insurance_certificate", "Insurance certificate"],
    ["permit_document", "Permit document"],
    ["other", "Other"],
  ];
  const submitDocumentRequest = async (event) => {
    event.preventDefault();
    const name = docRequest.name.trim();
    if (!selectedPerson?.id || selectedPerson.kind === "invite") return toast.error("Select an accepted participant first.");
    if (!name) return toast.error("Enter the document name.");
    setDocRequest((current) => ({ ...current, busy: true }));
    try {
      await api.post(`/participants/${selectedPerson.id}/documents`, {
        name,
        type: docRequest.type,
        status: "requested",
      });
      toast.success("Document requested.");
      setDocRequest({ open: false, name: "", type: "government_id", busy: false });
      await onRefresh?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setDocRequest((current) => ({ ...current, busy: false }));
    }
  };
  useEffect(() => {
    if (!selectedId && people[0]?.id) setSelectedId(people[0].id);
    if (selectedId && !people.some((person) => person.id === selectedId)) {
      setSelectedId(people[0]?.id || "");
    }
  }, [people, selectedId]);
  if (!canParticipantView && !canBookings) return null;
  return (
    <motion.div
      {...motionEnabled(shouldReduceMotion, sectionMotion)}
      className="overflow-hidden bg-white/90"
      data-testid="run-participants-panel"
    >
      <div className="grid min-h-[34rem] lg:grid-cols-[minmax(0,1.45fr)_minmax(21rem,0.85fr)]">
        <div className="min-w-0 border-b border-[#E2DFD7] p-3 sm:p-4 lg:border-b-0 lg:border-r">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-black text-[#17211D]">Participants</h2>
              <p className="mt-1 text-sm text-[#516259]">
                {participantSummary ? `${participantSummary.total || 0} participants` : "Loading participants"}
                {participantSummary?.pending ? ` - ${participantSummary.pending} pending invites` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canParticipantView && <Btn type="button" onClick={onOpenParticipants} className="min-h-9 px-3 py-1.5 text-xs"><UserPlus className="h-4 w-4" /> Manage</Btn>}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 xl:flex-row">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={participantQuery}
                onChange={(event) => setParticipantQuery(event.target.value)}
                placeholder="Search by name, phone, or email..."
                className="min-h-10 w-full rounded-xl border border-[#E2DFD7] bg-[#FFFCF7] px-3 py-2 pl-9 text-[13px] text-[#17211D] shadow-sm outline-none transition focus:border-[#2D7A65] focus:ring-2 focus:ring-[#BFD5C6]"
                data-testid="run-participant-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {filters.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setStatusFilter(item.id)}
                  className={`min-h-10 rounded-xl border px-3 text-[11px] font-black transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] ${statusFilter === item.id ? "border-[#17211D] bg-[#17211D] text-white" : "border-[#E2DFD7] bg-[#FFFCF7] text-[#516259] hover:border-[#BFD5C6]"}`}
                  data-testid={`run-participant-filter-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {participantSummary?.error ? (
            <div className="mt-4 space-y-2">
              {participantSummary?.error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">{participantSummary.error}</p>}
            </div>
          ) : !canParticipantView ? (
            <p className="mt-4 rounded-md border border-dashed border-[#D8D2C4] bg-[#FFFCF7] p-4 text-sm text-slate-500">
              Participant access is required to show traveller status.
            </p>
          ) : filteredPeople.length ? (
            <div className="mt-4 overflow-hidden rounded-xl border border-[#E2DFD7]">
              <div className="hidden max-h-[31rem] overflow-y-auto lg:block">
                <table className="w-full text-sm" data-testid="run-participant-table">
                  <thead className="sticky top-0 z-10 border-b border-[#E2DFD7] bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.12em] text-[#516259]">
                    <tr>
                      <th className="px-3 py-2.5">Name / Contact</th>
                      {canBookings && <th className="px-3 py-2.5">Payment</th>}
                      {canDocs && <th className="px-3 py-2.5">Docs</th>}
                      <th className="px-3 py-2.5">Last check-in</th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  </thead>
                  <motion.tbody
                    {...motionEnabled(shouldReduceMotion, {
                      initial: "hidden",
                      animate: "show",
                      variants: {
                        hidden: {},
                        show: { transition: { staggerChildren: 0.035 } },
                      },
                    })}
                  >
                    {filteredPeople.map((person) => {
                      const paymentStatus = person.payment_status || "none";
                      const docs = participantDocSummary(person, documents, trip);
                      const liveShare = participantLocationShare(person, locationShares);
                      const selected = selectedPerson?.id === person.id;
                      return (
                        <motion.tr
                          key={`${person.kind}-${person.id || person.email}`}
                          {...motionEnabled(shouldReduceMotion, {
                            variants: {
                              hidden: { opacity: 0, x: -8 },
                              show: { opacity: 1, x: 0, transition: { duration: 0.22, ease: RUN_EASE } },
                            },
                            whileHover: { backgroundColor: "rgba(231, 242, 237, 0.48)" },
                          })}
                          className={`tp-run-table-row cursor-pointer border-t border-[#ECEAE4] ${selected ? "bg-[#E7F2ED]/70" : ""}`}
                          onClick={() => setSelectedId(person.id)}
                          data-testid={`run-participant-row-${person.id || person.email}`}
                        >
                          <td className="px-3 py-3 align-top">
                            <div className="flex items-center gap-3">
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E7F2ED] text-xs font-black text-[#2D7A65] ring-1 ring-[#BFD5C6]">
                                {participantInitials(person)}
                              </span>
                              <span className="min-w-0">
                                <span className="block font-black text-[#17211D]">{person.display_name || person.email || "Traveller"}</span>
                                <span className="block truncate text-xs text-slate-500">{participantPhone(person) || participantEmail(person) || "Contact not set"}</span>
                              </span>
                            </div>
                          </td>
                          {canBookings && (
                            <td className="px-3 py-3 align-top">
                              <StatusPill tone={paymentTone(paymentStatus)}>{paymentLabel(paymentStatus)}</StatusPill>
                              {person.amount_due_minor > 0 && <p className="mt-1 text-xs font-bold text-rose-700">{inr(person.amount_due_minor)} due</p>}
                            </td>
                          )}
                          {canDocs && <td className="px-3 py-3 align-top"><StatusPill tone={docs.tone}>{docs.label}</StatusPill></td>}
                          <td className="px-3 py-3 align-top">
                            <CheckInStatus person={person} />
                            {liveShare && <p className="mt-1"><StatusPill tone={liveShare.paused ? "amber" : "green"}>{liveShare.paused ? "Location paused" : "Live location"}</StatusPill></p>}
                          </td>
                          <td className="px-3 py-3 text-right align-top">
                            <ChevronRight className="ml-auto h-4 w-4 text-[#516259]" />
                          </td>
                        </motion.tr>
                      );
                    })}
                  </motion.tbody>
                </table>
              </div>
              <motion.div
                {...motionEnabled(shouldReduceMotion, {
                  initial: "hidden",
                  animate: "show",
                  variants: {
                    hidden: {},
                    show: { transition: { staggerChildren: 0.05 } },
                  },
                })}
                className="max-h-[31rem] space-y-3 overflow-y-auto p-3 lg:hidden"
              >
                {filteredPeople.map((person) => {
                  const paymentStatus = person.payment_status || "none";
                  const docs = participantDocSummary(person, documents, trip);
                  const liveShare = participantLocationShare(person, locationShares);
                  const selected = selectedPerson?.id === person.id;
                  return (
                    <motion.article
                      key={`${person.kind}-${person.id || person.email}`}
                      {...motionEnabled(shouldReduceMotion, {
                        variants: {
                          hidden: { opacity: 0, y: 10 },
                          show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: RUN_EASE } },
                        },
                        whileHover: { y: -2 },
                      })}
                      className={`rounded-lg border p-3 ${selected ? "border-[#2D7A65] bg-[#E7F2ED]" : "border-[#ECEAE4] bg-[#FFFCF7]"}`}
                      onClick={() => setSelectedId(person.id)}
                      data-testid={`run-participant-card-${person.id || person.email}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E7F2ED] text-xs font-black text-[#2D7A65] ring-1 ring-[#BFD5C6]">
                            {participantInitials(person)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-black text-[#17211D]">{person.display_name || person.email || "Traveller"}</p>
                            <p className="truncate text-xs text-slate-500">{participantPhone(person) || participantEmail(person) || "Contact not set"}</p>
                          </div>
                        </div>
                        <StatusPill tone={inviteTone(person)}>
                          {inviteLabel(person)}
                        </StatusPill>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        {canBookings && <div><span className="block font-black uppercase text-[#516259]">Payment</span><StatusPill tone={paymentTone(paymentStatus)}>{paymentLabel(paymentStatus)}</StatusPill></div>}
                        {canDocs && <div><span className="block font-black uppercase text-[#516259]">Docs</span><StatusPill tone={docs.tone}>{docs.label}</StatusPill></div>}
                        <div><span className="block font-black uppercase text-[#516259]">Check-in</span><CheckInStatus person={person} /></div>
                        {liveShare && <div><span className="block font-black uppercase text-[#516259]">Live</span><StatusPill tone={liveShare.paused ? "amber" : "green"}>{liveShare.paused ? "Paused" : "Location on"}</StatusPill></div>}
                      </div>
                    </motion.article>
                  );
                })}
              </motion.div>
            </div>
          ) : (
            <p className="mt-4 rounded-md border border-dashed border-[#D8D2C4] bg-[#FFFCF7] p-4 text-sm text-slate-500">
              {people.length ? "No travellers match this search." : "No travellers yet. Add participants or invite interested people."}
            </p>
          )}
        </div>

        <aside className="flex min-h-0 flex-col bg-[#FFFCF7] p-3 sm:p-4">
          {!selectedPerson ? (
            <div className="flex min-h-[24rem] items-center justify-center rounded-xl border border-dashed border-[#D8D2C4] p-4 text-center text-sm text-[#516259]">
              Select a participant to see their details.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xl font-black text-[#17211D]">{selectedPerson.display_name || selectedPerson.email || "Traveller"}</p>
                  <p className="mt-1 text-xs text-[#516259]">{selectedPerson.kind === "invite" ? "Invite pending" : `Trip participant - ${trip.title}`}</p>
                </div>
                <StatusPill tone={selectedPerson.kind === "invite" ? "amber" : "green"}>{selectedPerson.kind === "invite" ? "Invited" : "Active"}</StatusPill>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Btn type="button" onClick={() => onOpenMessages(selectedPerson)} className="min-h-9 px-3 py-1.5 text-xs">
                  <MessageSquareText className="h-4 w-4" /> Message
                </Btn>
                {canDocs && (
                  <Btn
                    type="button"
                    variant="secondary"
                    onClick={() => setDocRequest({ open: true, name: "", type: "government_id", busy: false })}
                    disabled={selectedPerson.kind === "invite"}
                    className="min-h-9 px-3 py-1.5 text-xs"
                  >
                    <FileText className="h-4 w-4" /> Request document
                  </Btn>
                )}
              </div>

              {documentsError && <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">{documentsError}</p>}
              {locationSharesError && <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">{locationSharesError}</p>}

              <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
                <ParticipantDetailSection icon={Users} title="Contact">
                  <ParticipantDetailLine label="Phone" value={participantPhone(selectedPerson) || "Not set"} />
                  <ParticipantDetailLine label="Email" value={participantEmail(selectedPerson) || "Not set"} />
                </ParticipantDetailSection>

                {canBookings && (
                  <ParticipantDetailSection icon={Wallet} title="Payment">
                    <ParticipantDetailLine label="Status">
                      <StatusPill tone={paymentTone(selectedPerson.payment_status || "none")}>{paymentLabel(selectedPerson.payment_status || "none")}</StatusPill>
                    </ParticipantDetailLine>
                    <ParticipantDetailLine label="Due" value={selectedPerson.amount_due_minor != null ? inr(selectedPerson.amount_due_minor) : "Not available"} />
                  </ParticipantDetailSection>
                )}

                {canDocs && (
                  <ParticipantDetailSection icon={FileText} title="Documents">
                    <ParticipantDetailLine label="Status">
                      <StatusPill tone={selectedDocSummary.tone}>{selectedDocSummary.label}</StatusPill>
                    </ParticipantDetailLine>
                    {selectedDocChecklist.length ? (
                      <div className="space-y-2">
                        {selectedDocChecklist.map((item) => (
                          <div key={item.requirement.id || `${item.requirement.type}-${item.label}`} className="grid grid-cols-[minmax(0,1fr)_minmax(5.5rem,auto)_auto] items-center gap-2 text-sm">
                            <span className="min-w-0">
                              <span className="block truncate font-semibold text-[#17211D]">{item.label}</span>
                              <span className="text-xs text-[#516259]">
                                {item.required ? "Required" : "Optional"}
                                {item.requirement.due_date ? ` - due ${fmtDate(item.requirement.due_date)}` : ""}
                              </span>
                            </span>
                            <span className="min-w-0 truncate text-right text-xs font-semibold text-[#516259]">{item.value}</span>
                            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${item.uploaded ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"}`} aria-label={item.uploaded ? `${item.label} uploaded` : `${item.label} missing`}>
                              {item.uploaded ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : selectedDocs.length ? selectedDocs.slice(0, 4).map((doc) => (
                      <ParticipantDetailLine key={doc.id} label={doc.type || "Document"}>
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate">{doc.name}</span>
                          <StatusPill tone={documentHasUpload(doc) ? "green" : doc.status === "rejected" ? "rose" : "amber"}>{doc.status}</StatusPill>
                        </span>
                      </ParticipantDetailLine>
                    )) : <p className="text-sm text-[#516259]">No trip document requirements configured yet.</p>}
                  </ParticipantDetailSection>
                )}

                <ParticipantDetailSection icon={MapPin} title="Live status">
                  <ParticipantDetailLine label="Check-in">
                    <CheckInStatus person={selectedPerson} />
                  </ParticipantDetailLine>
                  <ParticipantDetailLine label="Location">
                    {activeLocationShare ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <StatusPill tone={activeLocationShare.paused ? "amber" : "green"}>{activeLocationShare.paused ? "Paused" : "Sharing"}</StatusPill>
                        <span className="text-[#516259]">Until {compactDate(activeLocationShare.expires_at)}</span>
                      </span>
                    ) : <StatusPill tone="slate">No active share</StatusPill>}
                  </ParticipantDetailLine>
                </ParticipantDetailSection>

                <ParticipantDetailSection icon={PhoneCall} title="Emergency contact">
                  {emergencyContact ? (
                    <>
                      <ParticipantDetailLine label="Name" value={emergencyContact.name} />
                      <ParticipantDetailLine label="Relation" value={emergencyContact.relation} />
                      <ParticipantDetailLine label="Phone" value={emergencyContact.phone} />
                    </>
                  ) : <p className="text-sm text-[#516259]">No emergency contact available.</p>}
                </ParticipantDetailSection>

                <ParticipantDetailSection icon={Mail} title="Notes">
                  {selectedPerson.roster_flags?.length > 0 && (
                    <ParticipantDetailLine label="Flags" value={selectedPerson.roster_flags.join(", ")} />
                  )}
                  <ParticipantDetailLine label="Allocation" value={[allocation.type, allocation.label].filter(Boolean).join(" - ") || "Not set"} />
                  <ParticipantDetailLine label="Notes" value={allocation.notes || "No notes added."} />
                </ParticipantDetailSection>
              </div>
            </>
          )}
        </aside>
      </div>
      {docRequest.open && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#07120F]/60 p-4 backdrop-blur-sm" role="presentation" onClick={() => !docRequest.busy && setDocRequest({ open: false, name: "", type: "government_id", busy: false })}>
          <form
            onSubmit={submitDocumentRequest}
            className="w-full max-w-md rounded-2xl border border-[#E2DFD7] bg-[#FFFCF7] p-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#2D7A65]">Participant document</p>
                <h3 className="mt-1 text-xl font-black text-[#17211D]">Request document</h3>
                <p className="mt-1 text-sm text-[#516259]">{selectedPerson?.display_name || selectedPerson?.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setDocRequest({ open: false, name: "", type: "government_id", busy: false })}
                disabled={docRequest.busy}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#516259] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]"
                aria-label="Close document request"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Document name</span>
                <input
                  value={docRequest.name}
                  onChange={(event) => setDocRequest((current) => ({ ...current, name: event.target.value }))}
                  className="mt-1 min-h-11 w-full rounded-xl border border-[#E2DFD7] bg-white px-3 text-sm text-[#17211D] outline-none transition focus:border-[#2D7A65] focus:ring-2 focus:ring-[#BFD5C6]"
                  placeholder="Aadhaar card, medical certificate..."
                  disabled={docRequest.busy}
                  required
                />
              </label>
              <label className="block">
                <span className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Type</span>
                <select
                  value={docRequest.type}
                  onChange={(event) => setDocRequest((current) => ({ ...current, type: event.target.value }))}
                  className="mt-1 min-h-11 w-full rounded-xl border border-[#E2DFD7] bg-white px-3 text-sm text-[#17211D] outline-none transition focus:border-[#2D7A65] focus:ring-2 focus:ring-[#BFD5C6]"
                  disabled={docRequest.busy}
                >
                  {documentTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Btn type="button" variant="secondary" onClick={() => setDocRequest({ open: false, name: "", type: "government_id", busy: false })} disabled={docRequest.busy}>Cancel</Btn>
              <Btn type="submit" disabled={docRequest.busy}>{docRequest.busy ? "Requesting..." : "Request document"}</Btn>
            </div>
          </form>
        </div>,
        document.body,
      )}
    </motion.div>
  );
}

function PhotoStrip({ photos = [] }) {
  const visible = asArray(photos).slice(0, 5);
  const more = Math.max(0, asArray(photos).length - visible.length);
  if (!visible.length) return null;
  return (
    <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
      {visible.map((photo, index) => (
        <figure
          key={photo.id || `${photo.url}-${index}`}
          className="relative h-20 w-28 shrink-0 overflow-hidden rounded-lg border border-[#E2DFD7] bg-[#F7F6F2] shadow-sm"
        >
          <ParticipantMediaImage photo={photo} alt={photo.alt_text || ""} className="h-full w-full object-cover" loading="lazy" />
          {more > 0 && index === visible.length - 1 && (
            <figcaption className="absolute inset-0 flex items-center justify-center bg-[#17211D]/60 text-sm font-black text-white">+{more}</figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

function ActivityCard({ activity, index }) {
  const shouldReduceMotion = useReducedMotion();
  const locations = asArray(activity.activity_locations)
    .map((rel) => rel.location?.name || rel.location?.address || rel.name)
    .filter(Boolean);
  const packing = asArray(activity.packing_requirements).map((item) => item.name).filter(Boolean);
  const timing = [activity.start_time, activity.end_time].filter(Boolean).join(" - ");
  const locationLabel = locations[0] || activity.location || "";
  return (
    <motion.article
      {...motionEnabled(shouldReduceMotion, {
        initial: { opacity: 0, x: -14 },
        whileInView: { opacity: 1, x: 0 },
        viewport: { once: true, margin: "-50px" },
        whileHover: { y: -3, boxShadow: "0 16px 34px rgba(23, 33, 29, 0.13)" },
        transition: { delay: Math.min(index * 0.04, 0.2), duration: 0.36, ease: RUN_EASE },
      })}
      className="tp-run-activity-card rounded-lg border border-[#E2DFD7] bg-white p-3 shadow-sm"
      data-testid={`run-activity-${activity.id}`}
    >
      <div className="flex gap-3">
        <span className="tp-run-stop-dot flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2D7A65]/10 text-sm font-black text-[#2D7A65] ring-1 ring-[#BFD5C6]">{index + 1}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h4 className="text-base font-black text-[#17211D]">{activity.title || activity.name || "Activity"}</h4>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                {timing && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{timing}</span>}
                {durationLabel(activity.duration_min) && <span>{durationLabel(activity.duration_min)}</span>}
                {activity.difficulty && <span className="capitalize">{activity.difficulty}</span>}
                {locationLabel && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{locationLabel}</span>}
              </div>
            </div>
            {activity.activity_type && <motion.span {...motionEnabled(shouldReduceMotion, { whileHover: { y: -1, scale: 1.03 } })} className="tp-run-chip rounded-full bg-[#F7F1E5] px-2 py-1 text-[11px] font-bold capitalize text-[#516259] ring-1 ring-[#E2DFD7]">{activity.activity_type}</motion.span>}
          </div>
          {(activity.description || activity.public_description) && <p className="mt-3 text-sm leading-6 text-slate-700">{activity.description || activity.public_description}</p>}
          {locations.length > 1 && <p className="mt-2 text-xs text-slate-500">Route stops: {locations.join(" -> ")}</p>}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {activity.safety_notes && (
              <div className="rounded-md bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800 ring-1 ring-amber-200">
                <span className="font-black">Care: </span>{activity.safety_notes}
              </div>
            )}
            {activity.staff_only_notes && (
              <div className="rounded-md bg-[#F7F6F2] px-3 py-2 text-xs leading-5 text-[#516259] ring-1 ring-[#E2DFD7]">
                <span className="font-black">Staff notes: </span>{activity.staff_only_notes}
              </div>
            )}
            {packing.length > 0 && (
              <div className="rounded-md bg-[#E7F2ED] px-3 py-2 text-xs leading-5 text-[#245F50] ring-1 ring-[#BFD5C6] sm:col-span-2">
                <span className="font-black">Carry: </span>{packing.join(", ")}
              </div>
            )}
          </div>
          <PhotoStrip photos={activity.photos} />
        </div>
      </div>
    </motion.article>
  );
}

function LogisticsCard({ item, kind }) {
  const shouldReduceMotion = useReducedMotion();
  const Icon = kind === "accommodation" ? Bed : Bus;
  const title = vendorName(item);
  const phone = logisticsPhone(item);
  const timing = kind === "accommodation"
    ? [item.check_in || item.start_date, item.check_out || item.end_date].filter(Boolean).join(" to ")
    : [item.departure_at || item.start_date, item.arrival_at || item.end_date].filter(Boolean).map(compactDate).join(" to ");
  return (
    <motion.div
      {...motionEnabled(shouldReduceMotion, {
        initial: { opacity: 0, y: 12 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-40px" },
        whileHover: { y: -3, boxShadow: "0 14px 30px rgba(23, 33, 29, 0.12)" },
        transition: { duration: 0.34, ease: RUN_EASE },
      })}
      className="rounded-lg border border-[#E2DFD7] bg-white p-3 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <span className="tp-run-logistics-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E7F2ED] text-[#2D7A65] ring-1 ring-[#BFD5C6]"><Icon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate font-black text-[#17211D]">{title}</p>
              <p className="mt-0.5 text-xs capitalize text-slate-500">{kind} {item.public_visibility || item.visibility ? `- ${item.public_visibility || item.visibility}` : ""}</p>
            </div>
            <CallButton phone={phone} name={title} />
          </div>
          {vendorMeta(item) && <p className="mt-1 text-xs leading-5 text-slate-600">{vendorMeta(item)}</p>}
          {timing && <p className="mt-1 text-xs font-semibold text-[#516259]">{timing}</p>}
          {item.trip_specific_notes && <p className="mt-2 whitespace-pre-line text-xs leading-5 text-slate-600">{item.trip_specific_notes}</p>}
        </div>
      </div>
    </motion.div>
  );
}

function GuideSummary({ trip, members, assignments }) {
  const shouldReduceMotion = useReducedMotion();
  const assignedIds = [
    trip.primary_guide_user_id,
    ...asArray(trip.coordinator_user_ids),
    ...asArray(trip.staff_user_ids),
  ].filter(Boolean);
  const assignedMembers = assignedIds
    .map((id) => members.find((member) => member.user_id === id) || { user_id: id, full_name: id })
    .filter(Boolean);
  const localGuides = assignments.filter((assignment) => assignment.vendor?.category === "local guide");
  return (
    <motion.aside
      {...motionEnabled(shouldReduceMotion, {
        ...sectionMotion,
        transition: { delay: 0.08, duration: 0.48, ease: RUN_EASE },
        whileHover: { y: -2 },
      })}
      className="flex h-full min-h-[32rem] flex-col overflow-hidden rounded-2xl border border-[#BFD5C6] bg-[#FFFCF7]/85"
      data-testid="run-team-guide-summary"
    >
      <div className="flex items-center justify-between gap-3 border-b border-[#E2DFD7] bg-[#E7F2ED] px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-black text-[#17211D]">
          <Users className="h-4 w-4 text-[#2D7A65]" /> Field contacts
        </h3>
        <span className="tp-run-status-pulse rounded-full bg-white px-2.5 py-1 text-[11px] font-black text-[#2D7A65] ring-1 ring-[#BFD5C6]">
          {assignedMembers.length + localGuides.length}
        </span>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#516259]">Team</p>
          {assignedMembers.length ? (
            <div className="mt-2 space-y-2">
              {assignedMembers.map((member) => {
                const profile = member.guide_profile || {};
                const name = memberName(member);
                const contactPhone = member.phone || profile.phone || profile.whatsapp;
                const roleChips = memberRoles(member).slice(0, 3);
                const detailChips = [profile.experience, ...asArray(profile.languages)].filter(Boolean).slice(0, 2);
                return (
                  <article key={member.user_id} className="tp-run-contact-card rounded-lg bg-white/75 px-3 py-2 ring-1 ring-[#ECEAE4]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="font-semibold leading-5 text-[#17211D]">{name}</p>
                          {trip.primary_guide_user_id === member.user_id && (
                            <span className="tp-run-status-pulse inline-flex items-center gap-1 rounded-full bg-[#E7F2ED] px-2 py-0.5 text-[10px] font-black text-[#2D7A65] ring-1 ring-[#BFD5C6]">
                              <Star className="h-3 w-3" /> Lead
                            </span>
                          )}
                        </div>
                        {(roleChips.length > 0 || detailChips.length > 0) && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {roleChips.map((chip) => <RoleBadge key={chip} role={chip} />)}
                            {detailChips.map((chip) => (
                              <motion.span {...motionEnabled(shouldReduceMotion, { whileHover: { y: -1, scale: 1.03 } })} key={chip} className="tp-run-chip rounded-full bg-[#F7F1E5] px-2 py-1 text-[11px] font-bold capitalize leading-none text-[#516259] ring-1 ring-[#E2DFD7]">
                                {chip}
                              </motion.span>
                            ))}
                          </div>
                        )}
                      </div>
                      <CallButton phone={contactPhone} name={name} />
                    </div>
                  </article>
                );
              })}
            </div>
          ) : <p className="mt-2 text-sm text-slate-500">No team members assigned.</p>}
        </div>

        <div className="border-t border-[#E2DFD7] pt-3">
          <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#516259]">Local guides</p>
          {localGuides.length ? (
            <div className="mt-2 space-y-2">
              {localGuides.map((assignment) => {
                const vendor = assignment.vendor || {};
                const name = vendor.name || "Local guide vendor";
                const logistics = [vendor.region, vendor.address].filter(Boolean).join(" - ");
                const services = asArray(vendor.service_types).join(", ");
                const schedule = [assignment.start_date, assignment.end_date].filter(Boolean).join(" to ");
                const chips = [
                  assignment.status,
                  assignment.assignment_type,
                  vendor.capacity != null ? `${vendor.capacity} capacity` : "",
                ].filter(Boolean);
                return (
                  <article key={assignment.id} className="tp-run-contact-card rounded-lg bg-white/75 px-3 py-2 ring-1 ring-[#ECEAE4]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold leading-5 text-[#17211D]">{name}</p>
                        <RatingStars value={vendor.rating} />
                        <DetailLine icon={MapPin}>{logistics}</DetailLine>
                        <DetailLine icon={Languages}>{services}</DetailLine>
                        {schedule && <p className="mt-1 text-xs font-semibold text-[#516259]">{schedule}</p>}
                        {chips.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {chips.slice(0, 4).map((chip) => (
                              <motion.span {...motionEnabled(shouldReduceMotion, { whileHover: { y: -1, scale: 1.03 } })} key={chip} className="tp-run-chip rounded-full bg-[#F7F1E5] px-2 py-1 text-[11px] font-bold capitalize leading-none text-[#516259] ring-1 ring-[#E2DFD7]">
                                {String(chip).replace(/_/g, " ")}
                              </motion.span>
                            ))}
                          </div>
                        )}
                        {assignment.trip_specific_notes && (
                          <p className="mt-2 rounded-md bg-[#F7F6F2] px-2.5 py-2 text-xs leading-5 text-[#516259]">{assignment.trip_specific_notes}</p>
                        )}
                      </div>
                      <CallButton phone={vendor.contact_phone} name={name} />
                    </div>
                  </article>
                );
              })}
            </div>
          ) : <p className="mt-2 text-sm text-slate-500">No local guide vendors assigned.</p>}
        </div>
      </div>
    </motion.aside>
  );
}

function dayCollapseKey(day, index) {
  return String(day.id || day.day_number || index);
}

function OpsSummaryButton({ icon: Icon, title, countText, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-w-0 items-center gap-3 rounded-lg border border-[#E2DFD7] bg-white px-3 py-2.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#BFD5C6] hover:bg-[#FFFCF7] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]"
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#E7F2ED] text-[#2D7A65] ring-1 ring-[#CFE2D8]">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center justify-between gap-2">
          <span className="truncate text-sm font-black text-[#17211D]">{title}</span>
          <span className="shrink-0 rounded-full bg-[#FFFCF7] px-2.5 py-1 text-[11px] font-black text-[#516259] ring-1 ring-[#E2DFD7]">{countText}</span>
        </span>
        <span className="mt-1 block truncate text-xs font-semibold text-[#718078]">{subtitle}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-[#718078] transition group-hover:translate-x-0.5 group-hover:text-[#2D7A65]" />
    </button>
  );
}

function dayVendorRows(day) {
  const assignments = asArray(day?.vendor_assignments);
  const transport = [
    ...asArray(day?.transportation),
    ...assignments.filter((item) => item.vendor?.category === "transport"),
  ];
  const accommodations = [
    ...asArray(day?.accommodations),
    ...assignments.filter((item) => item.vendor?.category === "accommodation"),
  ];
  const otherVendors = assignments.filter((item) => !["transport", "accommodation", "local guide"].includes(item.vendor?.category));
  const localGuides = assignments.filter((item) => item.vendor?.category === "local guide");
  return uniqueById([...transport, ...accommodations, ...localGuides, ...otherVendors]);
}

function vendorDistinctKey(item) {
  const vendor = item?.vendor || {};
  const explicitId = vendor.id || vendor._id || item?.vendor_id || item?.vendor?.vendor_id || "";
  if (explicitId) return `id:${explicitId}`;
  return [
    vendorResourceName(item),
    vendor.category || item?.category || item?.type || item?.assignment_type,
    logisticsPhone(item),
    vendor.contact_email || item?.contact_email || item?.contact_details?.email,
  ].map((value) => String(value || "").trim().toLowerCase()).filter(Boolean).join("|");
}

function dayTagFor(day) {
  return day?.day_number ? `D${day.day_number}` : day?.title || "Day";
}

function distinctTripVendors(days) {
  const vendors = new globalThis.Map();
  asArray(days).forEach((day) => {
    const dayTag = dayTagFor(day);
    dayVendorRows(day).forEach((item) => {
      const key = vendorDistinctKey(item);
      if (!key) return;
      const current = vendors.get(key);
      if (!current) {
        vendors.set(key, {
          ...item,
          tagged_days: dayTag ? [dayTag] : [],
        });
        return;
      }
      if (dayTag && !asArray(current.tagged_days).includes(dayTag)) {
        current.tagged_days = [...asArray(current.tagged_days), dayTag];
      }
    });
  });
  return [...vendors.values()];
}

function dayPackingRows(day) {
  const activities = asArray(day?.activities);
  return uniqueById([
    ...asArray(day?.packing_requirements),
    ...activities.flatMap((activity) => asArray(activity.packing_requirements)),
  ]);
}

function tripLinkedPackingRows(days, tripPackingItems) {
  const dayIds = new Set(asArray(days).map((day) => String(day?.id || "")).filter(Boolean));
  const activityIds = new Set(asArray(days).flatMap((day) => asArray(day?.activities)).map((activity) => String(activity?.id || "")).filter(Boolean));
  return asArray(tripPackingItems).filter((item) => {
    if (item?.participant_id) return false;
    const itemDayIds = [item?.itinerary_day_id, ...asArray(item?.itinerary_day_ids)].filter(Boolean).map(String);
    const itemActivityId = String(item?.activity_id || "");
    const tripWide = itemDayIds.length === 0 && !itemActivityId;
    return tripWide || itemDayIds.some((id) => dayIds.has(id)) || (itemActivityId && activityIds.has(itemActivityId));
  });
}

function TripOperationsPanel({ days = [], documents = [], tripPackingItems = [] }) {
  const [resourceModal, setResourceModal] = useState(null);
  const tripVendors = distinctTripVendors(days);
  const tripDocuments = asArray(documents).filter((document) => !document.owner_type || document.owner_type === "trip");
  const packing = packingDisplayRows([
    ...tripLinkedPackingRows(days, tripPackingItems),
    ...asArray(days).flatMap(dayPackingRows),
  ]);
  const vendorItems = tripVendors.map((item) => ({
    title: vendorResourceName(item),
    subtitle: asArray(item.tagged_days).join(", "),
    icon: item?.vendor?.category === "accommodation" || item?.property_name ? Bed : item?.vendor?.category === "transport" || item?.provider ? Bus : BriefcaseBusiness,
    record: item,
  }));
  const packingItems = packing.map((item) => ({
    title: item.name || "Packing item",
    subtitle: item.description || item.category || "",
    icon: Backpack,
    record: item,
  }));
  const documentItems = tripDocuments.map((document) => ({
    title: documentResourceName(document),
    subtitle: [String(document.type || "").replace(/_/g, " "), document.status].filter(Boolean).join(" - "),
    icon: FileText,
    record: document,
  }));
  const resources = [
    vendorItems.length ? {
      icon: BriefcaseBusiness,
      title: "Vendors",
      countText: countLabel(vendorItems.length, "vendor"),
      subtitle: vendorItems.slice(0, 2).map((item) => item.title).join(", "),
      resource: {
        type: "vendors",
        title: "Vendors",
        singular: "vendor",
        icon: BriefcaseBusiness,
        items: vendorItems,
        emptyText: "",
      },
    } : null,
    packingItems.length ? {
      icon: Backpack,
      title: "Packing",
      countText: countLabel(packingItems.length, "item"),
      subtitle: packingItems.slice(0, 2).map((item) => item.title).join(", "),
      resource: {
        type: "packing",
        title: "Packing",
        singular: "item",
        icon: Backpack,
        items: packingItems,
        emptyText: "",
      },
    } : null,
    documentItems.length ? {
      icon: FileText,
      title: "Documents",
      countText: countLabel(documentItems.length, "doc"),
      subtitle: documentItems.slice(0, 2).map((item) => item.title).join(", "),
      resource: {
        type: "documents",
        title: "Documents",
        singular: "doc",
        icon: FileText,
        items: documentItems,
        emptyText: "",
      },
    } : null,
  ].filter(Boolean);

  if (!resources.length) return null;

  return (
    <section className="tp-run-day-card rounded-[1.15rem] border border-[#BFD5C6] bg-[#FFFAF1] p-3 shadow-sm sm:p-4" data-testid="run-trip-operations-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xl font-black text-[#17211D]">Trip operations</h3>
      </div>
      <div className={`grid gap-3 ${resources.length === 1 ? "" : resources.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}>
        {resources.map((item) => (
          <OpsSummaryButton
            key={item.title}
            icon={item.icon}
            title={item.title}
            countText={item.countText}
            subtitle={item.subtitle}
            onClick={() => setResourceModal(item.resource)}
          />
        ))}
      </div>
      <ResourceListModal resource={resourceModal} onClose={() => setResourceModal(null)} />
    </section>
  );
}

function formattedDetail(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

function packingDisplayRows(items = []) {
  const seen = new Set();
  return asArray(items).flatMap((item, itemIndex) => {
    const names = String(item?.name || "")
      .split(/[,;\n]+/)
      .map((name) => name.trim().replace(/\s+/g, " "))
      .filter(Boolean);
    const safeNames = names.length ? names : [item?.name || "Packing item"];
    return safeNames.map((name, nameIndex) => {
      const key = name.toLowerCase();
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        ...item,
        id: item?.id ? `${item.id}-${nameIndex}` : `packing-${itemIndex}-${nameIndex}`,
        name,
      };
    }).filter(Boolean);
  });
}

function vendorDetailRows(item = {}) {
  const vendor = item.vendor || {};
  const contact = item.contact_details || {};
  const timing = [
    [item.start_date, item.end_date].filter(Boolean).join(" to "),
    [item.departure_at, item.arrival_at].filter(Boolean).map(compactDate).join(" to "),
    [item.check_in, item.check_out].filter(Boolean).join(" to "),
  ].find(Boolean);
  return [
    ["Tagged days", item.tagged_days],
    ["Category", vendor.category || item.category || item.type || item.assignment_type],
    ["Assignment", item.assignment_type],
    ["Status", item.status],
    ["Visibility", item.public_visibility || item.visibility],
    ["Schedule", timing],
    ["Region", vendor.region || item.region],
    ["Address", vendor.address || item.address],
    ["Services", vendor.service_types],
    ["Contact name", vendor.contact_name || item.contact_name],
    ["Phone", logisticsPhone(item)],
    ["Email", vendor.contact_email || item.contact_email || contact.email],
    ["Website", vendor.website || item.website || item.ticket_url],
    ["Capacity", vendor.capacity],
    ["Rating", vendor.rating != null && vendor.rating !== "" ? `${vendor.rating}/5` : ""],
    ["Charges", vendor.charges ?? item.charges ?? item.cost_minor],
    ["Booking reference", item.booking_reference || item.confirmation_number],
    ["Vehicle details", item.vehicle_details],
    ["Room details", [item.room_type, item.room_count != null ? `${item.room_count} room${Number(item.room_count) === 1 ? "" : "s"}` : ""].filter(Boolean).join(" - ")],
    ["Tags", vendor.tags],
    ["Vendor details", vendor.details],
    ["Trip notes", item.trip_specific_notes || item.notes],
    ["Pricing notes", vendor.pricing_notes],
    ["Internal notes", vendor.internal_notes],
  ].map(([label, value]) => [label, formattedDetail(value)]).filter(([, value]) => value);
}

function DetailGrid({ rows }) {
  const visibleRows = asArray(rows).filter(([, value]) => value);
  if (!visibleRows.length) return null;
  return (
    <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
      {visibleRows.map(([label, value]) => (
        <div key={label} className={String(value).length > 88 ? "sm:col-span-2" : ""}>
          <dt className="text-[10px] font-black uppercase tracking-[0.16em] text-[#718078]">{label}</dt>
          <dd className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold leading-6 text-[#17211D]">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function VendorDetailPanel({ item }) {
  if (!item) {
    return <div className="rounded-xl border border-dashed border-[#D8D2C4] bg-white/70 p-6 text-sm font-semibold text-[#718078]">Select a vendor to view details.</div>;
  }
  const vendor = item.vendor || {};
  const title = vendorResourceName(item);
  const phone = logisticsPhone(item);
  const callHref = phoneHref(phone);
  const email = vendor.contact_email || item.contact_email || item.contact_details?.email;
  const meta = [vendor.category || item.category || item.type || item.assignment_type, vendor.region || item.region].filter(Boolean).join(" - ");
  const rows = vendorDetailRows(item);
  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-[#E2DFD7] bg-white shadow-sm">
      <div className="border-b border-[#E2DFD7] bg-gradient-to-br from-[#F8F4EA] to-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#2D7A65]">Vendor details</p>
            <h3 className="mt-1 break-words text-2xl font-black leading-tight text-[#17211D]">{title}</h3>
            {meta && <p className="mt-2 text-sm font-semibold capitalize text-[#516259]">{meta}</p>}
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {callHref && (
              <a href={callHref} className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-[#2D7A65] px-4 text-sm font-black text-white shadow-sm shadow-[#2D7A65]/20 transition hover:-translate-y-0.5 hover:bg-[#245F50] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] focus-visible:ring-offset-2">
                <PhoneCall className="h-4 w-4" /> Call {phone}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-[#D8D2C4] bg-white px-4 text-sm font-black text-[#17211D] shadow-sm transition hover:-translate-y-0.5 hover:bg-[#F7F1E5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]">
                <Mail className="h-4 w-4" /> Email
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        <DetailGrid rows={rows} />
      </div>
    </section>
  );
}

async function openAttachedDocument(documentRecord) {
  const preview = window.open("", "_blank");
  if (!preview) {
    toast.error("Your browser blocked the document preview. Please allow popups and try again.");
    return;
  }
  try {
    preview.document.title = documentRecord.file_name || documentRecord.name || "Document preview";
    preview.document.body.textContent = "Opening document...";
    const response = await api.get(`/documents/${documentRecord.id}/file`, { responseType: "blob" });
    const fileUrl = URL.createObjectURL(response.data);
    preview.location.replace(fileUrl);
    window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60 * 60 * 1000);
  } catch (err) {
    preview.close();
    toast.error(formatApiError(err, "Could not open this document."));
  }
}

function GenericResourceDetailPanel({ item, emptyLabel, type }) {
  if (!item) {
    return <div className="rounded-xl border border-dashed border-[#D8D2C4] bg-white/70 p-6 text-sm font-semibold text-[#718078]">Select an item to view details.</div>;
  }
  const record = item.record || {};
  const hasDocumentFile = type === "documents" && Boolean(record.id && (record.file_name || record.file_size_bytes || record.current_version_id || record.storage_key));
  const rows = [
    ["Name", item.title],
    ["Summary", item.subtitle],
    ["Category", record.category || record.type],
    ["Status", record.status],
    ["Description", record.description],
    ["Expiry", record.expiry_date],
    ["File", record.file_name],
    ["Link", record.link],
  ].map(([label, value]) => [label, formattedDetail(value)]).filter(([, value]) => value);
  const hasActions = type === "documents" && (record.link || hasDocumentFile);
  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-[#E2DFD7] bg-white shadow-sm">
      <div className="border-b border-[#E2DFD7] bg-gradient-to-br from-[#F8F4EA] to-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#2D7A65]">{emptyLabel}</p>
            <h3 className="mt-1 break-words text-2xl font-black leading-tight text-[#17211D]">{item.title}</h3>
            {item.subtitle && <p className="mt-2 text-sm font-semibold text-[#516259]">{item.subtitle}</p>}
          </div>
          {hasActions && (
            <div className="flex shrink-0 flex-wrap gap-2">
              {record.link && (
                <a href={record.link} target="_blank" rel="noreferrer" className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-[#D8D2C4] bg-white px-4 text-sm font-black text-[#17211D] shadow-sm transition hover:-translate-y-0.5 hover:bg-[#F7F1E5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]">
                  <FileText className="h-4 w-4" /> Open link
                </a>
              )}
              {hasDocumentFile && (
                <button type="button" onClick={() => openAttachedDocument(record)} className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-[#2D7A65] px-4 text-sm font-black text-white shadow-sm shadow-[#2D7A65]/20 transition hover:-translate-y-0.5 hover:bg-[#245F50] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] focus-visible:ring-offset-2">
                  <FileText className="h-4 w-4" /> Open attached file
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="p-5 sm:p-6">
        <DetailGrid rows={rows} />
      </div>
    </section>
  );
}

function ResourceListModal({ resource, onClose }) {
  const items = resource?.items || [];
  const [selectedIndex, setSelectedIndex] = useState(0);
  useEffect(() => {
    setSelectedIndex(0);
  }, [resource?.type]);
  useEffect(() => {
    if (!resource || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [resource]);

  if (!resource) return null;
  const selected = items[selectedIndex] || items[0] || null;
  const Icon = resource.icon || BriefcaseBusiness;
  const modal = (
    <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-[#07120F]/72 p-0 backdrop-blur-md sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={`${resource.title} list`} onClick={onClose}>
      <aside className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-t-[1.35rem] border border-white/30 bg-[#FFFBF3] shadow-2xl ring-1 ring-[#07120F]/10 sm:rounded-[1.35rem]" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-[#E2DFD7] bg-gradient-to-r from-[#10231D] via-[#1F5145] to-[#F0C14B] px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/95 text-[#2D7A65] shadow-sm ring-1 ring-white/60">
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#F0C14B]">Trip operations</p>
              <h3 className="mt-1 break-words text-2xl font-black leading-tight text-white">{resource.title}</h3>
              <p className="mt-1 text-sm font-semibold text-white/75">{countLabel(items.length, resource.singular || "item")}</p>
            </div>
          </div>
          <button type="button" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/35 bg-white/95 text-[#516259] shadow-sm transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" onClick={onClose} aria-label={`Close ${resource.title}`}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid max-h-[calc(92vh-5.25rem)] gap-0 overflow-y-auto lg:grid-cols-[minmax(18rem,0.36fr)_minmax(0,1fr)]">
          <div className="border-b border-[#E2DFD7] bg-[#F5EFE2] p-3 lg:max-h-[calc(92vh-5.25rem)] lg:overflow-y-auto lg:border-b-0 lg:border-r">
            {items.length ? items.map((item, itemIndex) => {
              const ItemIcon = item.icon || Icon;
              const active = itemIndex === selectedIndex;
              return (
                <button
                  key={`${resource.type}-${item.title}-${itemIndex}`}
                  type="button"
                  onClick={() => setSelectedIndex(itemIndex)}
                  className={`group mb-2 flex w-full min-w-0 items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] ${active ? "border-[#2D7A65] bg-white shadow-md shadow-[#17211D]/5" : "border-[#E2DFD7] bg-white/70 hover:-translate-y-0.5 hover:border-[#BFD5C6] hover:bg-white hover:shadow-sm"}`}
                >
                  <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[#2D7A65] ring-1 ${active ? "bg-[#E7F2ED] ring-[#9BC8B8]" : "bg-[#EAF3EF] ring-[#CFE2D8]"}`}><ItemIcon className="h-4 w-4" /></span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black text-[#17211D]">{item.title}</span>
                    {item.subtitle && <span className="mt-0.5 block truncate text-xs font-semibold text-[#718078]">{item.subtitle}</span>}
                  </span>
                  <ChevronRight className={`ml-auto h-4 w-4 shrink-0 transition ${active ? "text-[#2D7A65]" : "text-[#A8A296] group-hover:translate-x-0.5 group-hover:text-[#2D7A65]"}`} />
                </button>
              );
            }) : (
              <p className="rounded-xl border border-dashed border-[#D8D2C4] bg-white/70 p-4 text-sm font-semibold text-[#718078]">{resource.emptyText}</p>
            )}
          </div>
          <div className="p-4 sm:p-5">
            {resource.type === "vendors"
              ? <VendorDetailPanel item={selected?.record} />
              : <GenericResourceDetailPanel item={selected} emptyLabel={resource.title} type={resource.type} />}
          </div>
        </div>
      </aside>
    </div>
  );
  return createPortal(modal, document.body);
}

function DayOverview({ day, index, isCollapsed, onToggle }) {
  const shouldReduceMotion = useReducedMotion();
  const activities = asArray(day.activities);
  const metrics = [
    day.date ? { key: "date", label: fmtDate(day.date) } : null,
    day.distance_km != null ? { key: "distance", label: `${day.distance_km} km` } : null,
    day.elevation_gain_m != null ? { key: "elevation", label: `${day.elevation_gain_m} m gain` } : null,
    durationLabel(day.estimated_duration_min) ? { key: "duration", label: durationLabel(day.estimated_duration_min) } : null,
  ].filter(Boolean);
  const dayFacts = [
    asArray(day.meals).length ? { icon: Utensils, label: "Meals", value: joinText(day.meals, "") } : null,
    day.safety_notes ? { icon: ShieldAlert, label: "Care", value: day.safety_notes } : null,
  ].filter(Boolean);
  const routeStats = day.route_preview ? [
    (day.route_preview.status || day.route_preview.provider_status) ? { icon: Map, label: "Status", value: day.route_preview.status || day.route_preview.provider_status } : null,
    day.route_preview.distance_km != null ? { icon: Route, label: "Distance", value: `${day.route_preview.distance_km} km` } : null,
    durationLabel(day.route_preview.estimated_duration_min) ? { icon: Clock, label: "Duration", value: durationLabel(day.route_preview.estimated_duration_min) } : null,
  ].filter(Boolean) : [];
  return (
    <motion.section
      {...motionEnabled(shouldReduceMotion, {
        initial: { opacity: 0, y: 28, scale: 0.985 },
        whileInView: { opacity: 1, y: 0, scale: 1 },
        viewport: { once: true, margin: "-70px" },
        transition: { delay: Math.min(index * 0.07, 0.28), duration: 0.5, ease: RUN_EASE },
      })}
      className="tp-run-day-card overflow-hidden rounded-[1.15rem] border border-[#BFD5C6] bg-[#FFFAF1] shadow-sm"
      data-testid={`run-overview-day-${day.day_number}`}
    >
      <header className="border-b border-[#E2DFD7] bg-white/65">
        <div className="grid gap-3 p-3 sm:p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!isCollapsed}
            className="flex min-w-0 gap-3 rounded-lg text-left transition hover:bg-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] focus-visible:ring-offset-2 focus-visible:ring-offset-white/70"
          >
            <ChevronRight className={`mt-4 h-4 w-4 shrink-0 text-[#516259] transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
            <span className="tp-run-day-badge flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E5B85C] text-xs font-black text-[#17211D]">D{day.day_number}</span>
            <span className="min-w-0">
              <span className="block text-[11px] font-black uppercase tracking-[0.14em] text-[#516259]">Chapter {index + 1}</span>
              <span className="mt-0.5 block text-xl font-black text-[#17211D]">{day.title || `Day ${day.day_number}`}</span>
              {day.summary && <span className="mt-1 block text-sm font-semibold text-[#2D7A65]">{day.summary}</span>}
              {day.description && <span className="mt-2 block max-w-4xl text-sm font-medium leading-6 text-slate-700">{day.description}</span>}
            </span>
          </button>
          {metrics.length > 0 && (
            <div className="flex min-w-0 flex-wrap items-center gap-2 lg:max-w-[18rem] lg:justify-end">
              {metrics.map((metric) => (
              <span key={metric.key} className="tp-run-chip whitespace-nowrap rounded-full bg-white px-3 py-1 text-xs font-bold leading-none text-[#516259] ring-1 ring-[#E2DFD7]">
                {metric.label}
              </span>
              ))}
            </div>
          )}
        </div>
      </header>
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            key="day-body"
            {...motionEnabled(shouldReduceMotion, {
              initial: { height: 0, opacity: 0 },
              animate: { height: "auto", opacity: 1 },
              exit: { height: 0, opacity: 0 },
              transition: { duration: 0.24, ease: RUN_EASE },
            })}
            className="overflow-hidden"
          >
            <div className="space-y-3 p-3 sm:p-4">
              {day.photos?.length > 0 && <PhotoStrip photos={day.photos} />}
              {dayFacts.length > 0 && (
                <div className={`grid gap-3 ${dayFacts.length === 1 ? "" : "lg:grid-cols-2"}`}>
                  {dayFacts.map((fact) => <MiniStat key={fact.label} icon={fact.icon} label={fact.label} value={fact.value} />)}
                </div>
              )}

              {activities.length > 0 && (
                <div className="space-y-3">
                  <SectionHeader
                    icon={Flag}
                    title={`Today's flow - ${activities.length} stop${activities.length === 1 ? "" : "s"}`}
                  />
                  <div className="tp-run-flow-list space-y-3">
                    {activities.map((activity, activityIndex) => <ActivityCard key={activity.id || activityIndex} activity={activity} index={activityIndex} />)}
                  </div>
                </div>
              )}

              {routeStats.length > 0 && (
                <div className="rounded-lg border border-[#E2DFD7] bg-white p-3">
                  <SectionHeader icon={Route} title="Day route" />
                  <div className="grid gap-3 sm:grid-cols-4">
                    {routeStats.map((stat) => <MiniStat key={stat.label} icon={stat.icon} label={stat.label} value={stat.value} />)}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

function messageText(message) {
  return message?.message || message?.body || "";
}

function messageTitle(message) {
  return message?.title || String(message?.type || "Trip update").replace(/_/g, " ");
}

function messageAudience(message) {
  const audience = message?.audience || "all";
  if (audience === "participant") return "participant";
  if (audience === "team") return "guides";
  return audience;
}

function audienceMatches(message, audience) {
  const value = messageAudience(message);
  if (audience?.startsWith("participant:")) return value === "participant" && message.participant_id === audience.slice("participant:".length);
  if (audience === "participants") return value === "participants" || value === "all";
  if (audience === "guides") return value === "guides" || value === "all";
  return value === "all";
}

function tripTeamMembers(trip, members) {
  const assignedIds = [
    trip?.primary_guide_user_id,
    ...asArray(trip?.coordinator_user_ids),
    ...asArray(trip?.staff_user_ids),
  ].filter(Boolean);
  return assignedIds
    .map((id) => members.find((member) => member.user_id === id) || { user_id: id, full_name: id })
    .filter(Boolean);
}

function audienceSubtitle(audience, participantSummary, teamMembers, directParticipant) {
  if (audience?.startsWith("participant:")) {
    return directParticipant ? participantEmail(directParticipant) || participantPhone(directParticipant) || "Direct participant chat" : "Direct participant chat";
  }
  if (audience === "participants") {
    const total = participantSummary?.total;
    return total === 1 ? "1 participant" : `${total || 0} participants`;
  }
  if (audience === "guides") {
    const total = teamMembers.length;
    return total === 1 ? "1 team member" : `${total || 0} team members`;
  }
  return "Participants and guides";
}

function SimpleMessagesTab({ trip, canCommunicate, participantSummary, members, directParticipant, embedded = false, batchId }) {
  const initialAudience = directParticipant?.id ? `participant:${directParticipant.id}` : "participants";
  const [audience, setAudience] = useState(initialAudience);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const audiences = [
    { id: "participants", label: "Participants", icon: Users },
  ];
  const teamMembers = tripTeamMembers({ ...trip, ...effectiveTripStaffing(trip, batchId) }, members);
  const directParticipants = [
    ...(directParticipant ? [directParticipant] : []),
    ...messages
      .filter((message) => messageAudience(message) === "participant" && message.participant_id)
      .map((message) => ({
        id: message.participant_id,
        display_name: message.metadata?.target_participant_name || `Participant ${String(message.participant_id).slice(-4)}`,
      })),
  ].filter((person, index, list) => person.id && list.findIndex((item) => item.id === person.id) === index);
  const chatItems = [
    ...directParticipants.map((person) => ({
      id: `participant:${person.id}`,
      label: person.display_name || participantEmail(person) || "Participant",
      icon: Users,
      participant: person,
    })),
    ...audiences,
  ];

  const loadMessages = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get(`/trips/${trip.id}/messages`, { params: { batch_id: batchId === ALL_BATCHES_ID ? undefined : batchId } });
      setMessages(data.data || []);
    } catch (err) {
      setError(formatApiError(err, "Could not load trip messages."));
    } finally {
      setLoading(false);
    }
  }, [trip.id, batchId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => {
    if (!directParticipant?.id) return;
    setAudience(`participant:${directParticipant.id}`);
  }, [directParticipant?.id]);

  const activeAudience = chatItems.find((item) => item.id === audience) || chatItems[0] || audiences[0];
  const activeDirectParticipant = activeAudience?.participant || directParticipants.find((person) => `participant:${person.id}` === audience);
  const visibleMessages = messages
    .filter((message) => audienceMatches(message, audience))
    .slice(0, 8)
    .reverse();

  const sendMessage = async () => {
    const message = draft.trim();
    if (!message) return toast.error("Write a message first.");
    setSending(true);
    try {
      const directId = audience.startsWith("participant:") ? audience.slice("participant:".length) : "";
      await api.post(`/trips/${trip.id}/announcements`, {
        title: directId ? "Direct message" : audience === "guides" ? "Guide update" : "Trip update",
        message,
        audience: directId ? "participant" : audience,
        participant_id: directId || undefined,
        batch_id: batchId === ALL_BATCHES_ID ? undefined : batchId,
        priority: "normal",
        confirm_send: true,
        client_idempotency_key: `run-message-${trip.id}-${audience}-${Date.now()}`,
      });
      toast.success("Message sent.");
      setDraft("");
      await loadMessages();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const shellClass = embedded
    ? "overflow-hidden"
    : "overflow-hidden rounded-[1.5rem] border border-[#E2DFD7] bg-white/90 shadow-sm";

  return (
    <div className={shellClass} data-testid="run-messages-tab">
      <div className="grid min-h-[42rem] lg:grid-cols-[minmax(15rem,0.34fr)_minmax(0,1fr)]">
        <aside className="border-b border-[#E2DFD7] bg-[#FFFCF7] p-3 lg:border-b-0 lg:border-r">
          <h2 className="text-xl font-black text-[#17211D]">Messages</h2>
          <div className="mt-4 space-y-3">
            {chatItems.map((item) => {
              const Icon = item.icon;
              const selected = audience === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setAudience(item.id)}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] ${selected ? "border-[#2D7A65] bg-[#E7F2ED]" : "border-[#E2DFD7] bg-white hover:border-[#BFD5C6]"}`}
                  data-testid={`run-message-audience-${item.id}`}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E7F2ED] text-[#2D7A65]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-black text-[#17211D]">{item.label}</span>
                      <span className="mt-0.5 block truncate text-xs text-[#516259]">{audienceSubtitle(item.id, participantSummary, teamMembers, item.participant)}</span>
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-[#516259]" />
                </button>
              );
            })}
          </div>
        </aside>

        <div className="flex min-h-0 flex-col p-3">
          <div className="border-b border-[#E2DFD7] pb-3">
            <h3 className="text-xl font-black text-[#17211D]">{activeAudience.label}</h3>
            <p className="mt-0.5 text-sm text-[#516259]">{audienceSubtitle(activeAudience.id, participantSummary, teamMembers, activeDirectParticipant)}</p>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
            {loading ? (
              <LoadingState label="Loading messages..." />
            ) : error ? (
              <ErrorState message={error} onRetry={loadMessages} />
            ) : visibleMessages.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#D8D2C4] bg-[#FFFCF7] p-5 text-sm text-[#516259]">
                No messages for this audience yet.
              </div>
            ) : visibleMessages.map((message) => (
              <article key={message.id} className="grid gap-3 sm:grid-cols-[3rem_minmax(0,1fr)_auto]">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E7F2ED] text-sm font-black text-[#2D7A65]">
                  {messageAudience(message) === "participant" ? "D" : messageAudience(message) === "guides" ? "G" : messageAudience(message) === "participants" ? "P" : "A"}
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-black text-[#17211D]">{messageTitle(message)}</p>
                    <span className="rounded-full bg-[#E7F2ED] px-2 py-0.5 text-[11px] font-bold capitalize text-[#2D7A65]">
                      {messageAudience(message)}
                    </span>
                  </div>
                  <p className="mt-1 max-w-2xl rounded-xl bg-[#FFF5DD] px-4 py-3 text-sm leading-6 text-[#17211D]">
                    {messageText(message)}
                  </p>
                </div>
                <time className="text-xs font-semibold text-[#516259]">{compactDate(message.created_at)}</time>
              </article>
            ))}
          </div>

          <div className="mt-auto border-t border-[#E2DFD7] pt-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={canCommunicate ? "Write a message..." : "You can view messages, but cannot send."}
                disabled={!canCommunicate || sending}
                rows={3}
                className="min-h-24 flex-1 resize-none rounded-xl border border-[#E2DFD7] bg-white px-4 py-3 text-sm text-[#17211D] outline-none transition focus:border-[#2D7A65] focus:ring-2 focus:ring-[#BFD5C6] disabled:bg-[#F7F6F2]"
                data-testid="run-message-composer"
              />
              <Btn type="button" disabled={!canCommunicate || sending} onClick={sendMessage} className="self-stretch px-5 sm:self-end">
                <Send className="h-4 w-4" /> Send
              </Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RunOverviewTab({
  trip,
  canEdit,
  canCommunicate = false,
  initialTab = "itinerary",
  canParticipantView = false,
  canBookings = false,
  canFinance = false,
  canDocs = false,
  onSetup,
}) {
  const shouldReduceMotion = useReducedMotion();
  const [days, setDays] = useState(null);
  const [members, setMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [participantSummary, setParticipantSummary] = useState(null);
  const [participantDocuments, setParticipantDocuments] = useState({ data: [], error: "" });
  const [tripPackingItems, setTripPackingItems] = useState([]);
  const [locationShares, setLocationShares] = useState({ data: [], error: "" });
  const [error, setError] = useState("");
  const [runTab, setRunTab] = useState(initialTab === "communications" ? "itinerary" : initialTab);
  const [communicationsOpen, setCommunicationsOpen] = useState(initialTab === "communications");
  const [managementPanel, setManagementPanel] = useState("");
  const [directMessageParticipant, setDirectMessageParticipant] = useState(null);
  const [collapsedDays, setCollapsedDays] = useState({});
  const [selectedRunBatchId, setSelectedRunBatchId] = useState(ALL_BATCHES_ID);
  const [runBatchScope, setRunBatchScope] = useState({ availability: null, bookings: [], loading: false, error: "" });
  const collapseStorageKey = `trailpulse.runOverview.collapsedDays.${trip.id}`;

  useEffect(() => {
    if (initialTab === "communications") {
      setCommunicationsOpen(true);
    } else {
      setRunTab(initialTab);
      setCommunicationsOpen(false);
    }
  }, [initialTab]);

  useEffect(() => {
    try {
      const saved = globalThis.localStorage?.getItem(collapseStorageKey);
      setCollapsedDays(saved ? JSON.parse(saved) || {} : {});
    } catch {
      setCollapsedDays({});
    }
  }, [collapseStorageKey]);

  useEffect(() => {
    let alive = true;
    setRunBatchScope((current) => ({ ...current, loading: true, error: "" }));
    Promise.all([
      api.get(`/trips/${trip.id}/availability`).catch((err) => ({ error: formatApiError(err, "Could not load batch availability.") })),
      canBookings
        ? getAllPages(`/trips/${trip.id}/bookings`, { sort_by: "created_at", sort_dir: "desc" }).catch((err) => ({ error: formatApiError(err, "Could not load batch bookings.") }))
        : Promise.resolve({ data: { data: [] } }),
    ]).then(([availabilityResp, bookingsResp]) => {
      if (!alive) return;
      if (availabilityResp?.error) {
        setRunBatchScope({ availability: null, bookings: [], loading: false, error: availabilityResp.error });
        return;
      }
      setRunBatchScope({
        availability: availabilityResp?.data || null,
        bookings: bookingsResp?.error ? [] : bookingsResp?.data?.data || [],
        loading: false,
        error: bookingsResp?.error || "",
      });
    }).catch((err) => {
      if (!alive) return;
      setRunBatchScope({ availability: null, bookings: [], loading: false, error: formatApiError(err, "Could not load batch filter.") });
    });
    return () => {
      alive = false;
    };
  }, [canBookings, trip.id]);

  const toggleDayCollapsed = useCallback((day, index) => {
    const key = dayCollapseKey(day, index);
    setCollapsedDays((current) => {
      const next = { ...current, [key]: !current[key] };
      if (!next[key]) delete next[key];
      try {
        globalThis.localStorage?.setItem(collapseStorageKey, JSON.stringify(next));
      } catch {
        // Ignore storage failures; the toggle should still work for this session.
      }
      return next;
    });
  }, [collapseStorageKey]);

  const load = useCallback(async () => {
    setError("");
    try {
      const participantReq = canParticipantView
        ? getAllPages(`/trips/${trip.id}/participants`, { batch_id: selectedRunBatchId === ALL_BATCHES_ID ? undefined : selectedRunBatchId, sort_by: "created_at", sort_dir: "desc" }).catch((err) => ({ error: formatApiError(err, "Could not load participants.") }))
        : Promise.resolve(null);
      const documentReq = canDocs
        ? getAllPages(`/trips/${trip.id}/documents`, { sort_by: "updated_at", sort_dir: "desc" }).catch((err) => ({ error: formatApiError(err, "Could not load participant documents.") }))
        : Promise.resolve(null);
      const packingReq = api.get(`/trips/${trip.id}/packing-items`).catch((err) => ({ error: formatApiError(err, "Could not load packing items.") }));
      const locationShareReq = canParticipantView
        ? api.get(`/trips/${trip.id}/location-shares`, { params: { include_paused: true } }).catch((err) => ({ error: formatApiError(err, "Could not load live locations.") }))
        : Promise.resolve(null);
      const [dayResp, memberResp, staffProfileResp, assignmentResp, participantResp, documentResp, packingResp, locationShareResp] = await Promise.all([
        api.get(`/trips/${trip.id}/days`),
        canEdit ? api.get("/org/team-contacts").catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
        canEdit ? api.get("/org/staff-profiles").catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
        api.get(`/trips/${trip.id}/vendor-assignments`).catch(() => ({ data: { data: [] } })),
        participantReq,
        documentReq,
        packingReq,
        locationShareReq,
      ]);
      const staffProfiles = staffProfileResp.data.data || [];
      const profileByUserId = new globalThis.Map(staffProfiles.map((profile) => [profile.user_id, profile]));
      setDays(dayResp.data.data || []);
      setMembers((memberResp.data.data || []).map((member) => ({
        ...member,
        guide_profile: profileByUserId.get(member.user_id) || member.guide_profile || null,
      })));
      setAssignments(assignmentResp.data.data || []);
      if (canParticipantView) {
        setParticipantSummary(participantResp?.error ? { error: participantResp.error, recent: [], total: 0, pending: 0 } : {
          recent: participantResp?.data?.data || [],
          total: participantResp?.data?.pagination?.total ?? (participantResp?.data?.data || []).length,
          pending: participantResp?.data?.pending_invites_total ?? (participantResp?.data?.pending_invites || []).length,
          pendingInvites: participantResp?.data?.pending_invites || [],
        });
      } else {
        setParticipantSummary(null);
      }
      setParticipantDocuments(documentResp?.error ? { data: [], error: documentResp.error } : {
        data: documentResp?.data?.data || [],
        error: "",
      });
      setTripPackingItems(packingResp?.error ? [] : (packingResp?.data?.data || []).filter((item) => !item.participant_id));
      setLocationShares(locationShareResp?.error ? { data: [], error: locationShareResp.error } : {
        data: locationShareResp?.data?.data || [],
        error: "",
      });
    } catch (err) {
      setError(formatApiError(err, "Could not load operational overview."));
    }
  }, [canDocs, canEdit, canParticipantView, trip.id, selectedRunBatchId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!days) return <LoadingState label="Loading operational overview..." />;

  const visibleRunTabs = RUN_TABS.filter((item) => {
    if (item.id === "bookings") return canBookings;
    if (item.id === "enquiries") return canBookings;
    if (item.id === "budget") return canFinance;
    if (item.id === "participants") return canParticipantView || canBookings;
    return true;
  });
  const activeRunTab = visibleRunTabs.some((item) => item.id === runTab) ? runTab : (visibleRunTabs[0]?.id || "itinerary");
  const runBatches = asArray(runBatchScope.availability?.batches);
  const showRunBatchScope = runBatches.length > 0;
  const selectedRunBatchStats = runBatchStats(runBatchScope.availability, runBatchScope.bookings, selectedRunBatchId);
  const selectedRunBatch = selectedRunBatchStats.selectedBatch;
  const selectedRunBatchDate = selectedRunBatch ? runBatchDates(selectedRunBatch) : "All departures";
  const executionDays = departureDays(days, selectedRunBatch);
  const rosterIds = new Set((participantSummary?.recent || []).map((person) => person.id));
  const scopedDocuments = selectedRunBatch ? participantDocuments.data.filter((doc) => doc.owner_type !== "participant" || rosterIds.has(doc.owner_ref)) : participantDocuments.data;

  return (
    <motion.div
      {...motionEnabled(shouldReduceMotion, pageMotion)}
      className="tp-run-overview space-y-3"
      data-testid="run-overview-tab"
    >
      <motion.section
        {...motionEnabled(shouldReduceMotion, sectionMotion)}
        className="tp-paper overflow-clip rounded-[1.15rem] p-0"
        data-testid="run-dashboard-panel"
      >
        {showRunBatchScope && (
          <div className="border-b border-[#E2DFD7] bg-[#FFFCF7]/90 px-3 py-2.5">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <label className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span className="shrink-0 text-[10px] font-black uppercase tracking-[0.14em] text-[#516259]">Batch</span>
                <select
                className={`${inputCls} h-9 min-w-0 rounded-md text-[13px] font-black sm:w-64`}
                  value={selectedRunBatchId}
                  onChange={(event) => setSelectedRunBatchId(event.target.value)}
                  aria-label="Dashboard batch"
                >
                  <option value={ALL_BATCHES_ID}>All batches</option>
                  {runBatches.map((batch, index) => (
                    <option key={batch.id || batch.label || index} value={batch.id || ""}>{runBatchTitle(batch, index)}</option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="rounded-full bg-white px-3 py-1.5 font-bold text-[#516259] ring-1 ring-[#E2DFD7]">{selectedRunBatchDate}</span>
                <span className="rounded-full bg-white px-3 py-1.5 font-bold text-[#516259] ring-1 ring-[#E2DFD7]">
                  {selectedRunBatchStats.capacity ? `${selectedRunBatchStats.bookedPax}/${selectedRunBatchStats.capacity} seats` : `${selectedRunBatchStats.bookedPax} seats`}
                </span>
                <span className="rounded-full bg-emerald-50 px-3 py-1.5 font-black text-emerald-700 ring-1 ring-emerald-100">{inr(selectedRunBatchStats.amountPaidMinor)} collected</span>
                <span className={`rounded-full px-3 py-1.5 font-black ring-1 ${selectedRunBatchStats.pendingDuesMinor > 0 ? "bg-rose-50 text-rose-700 ring-rose-100" : "bg-white text-[#516259] ring-[#E2DFD7]"}`}>{inr(selectedRunBatchStats.pendingDuesMinor)} pending</span>
              </div>
            </div>
          </div>
        )}
        <div className="flex overflow-x-auto border border-x-0 border-t-0 border-[#E2DFD7] bg-white/75" role="tablist" aria-label="Trip dashboard sections">
          {visibleRunTabs.map((item) => {
            const Icon = item.icon;
            const active = activeRunTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setRunTab(item.id)}
                className={`flex min-h-10 shrink-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap border-r border-[#E2DFD7] px-3 text-[13px] font-black transition last:border-r-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2D7A65] ${active ? "bg-[#17211D] text-white" : "text-[#17211D] hover:bg-[#FFFCF7]"}`}
                data-testid={`run-tab-${item.id}`}
              >
                <Icon className="h-4 w-4" /> {item.label}
              </button>
            );
          })}
        </div>

        <div className={["messages", "participants", "budget", "bookings"].includes(activeRunTab) ? "" : "p-3 sm:p-4"}>
          {activeRunTab === "messages" && (
            <SimpleMessagesTab
              trip={trip}
              batchId={selectedRunBatchId}
              canCommunicate={canCommunicate}
              participantSummary={participantSummary}
              members={members}
              directParticipant={directMessageParticipant}
              embedded
            />
          )}

          {activeRunTab === "itinerary" && (
            days.length === 0 ? (
              <EmptyState
                title="No itinerary days planned"
                description="Open Setup to add day-wise activities, logistics, packing, photos, and route details."
                action={canEdit ? <Btn type="button" onClick={() => onSetup("itinerary")}><ChevronRight className="tp-run-button-icon h-4 w-4" /> Open setup</Btn> : null}
              />
            ) : (
              <motion.div
                {...motionEnabled(shouldReduceMotion, {
                  initial: "hidden",
                  animate: "show",
                  variants: {
                    hidden: {},
                    show: { transition: { staggerChildren: 0.1 } },
                  },
                })}
                className="space-y-4"
                data-testid="run-itinerary-panel"
              >
                <TripOperationsPanel
                  days={executionDays}
                  documents={scopedDocuments}
                  tripPackingItems={tripPackingItems}
                />
                {executionDays.map((day, index) => {
                  const key = dayCollapseKey(day, index);
                  return (
                    <DayOverview
                      key={day.id || index}
                      day={day}
                      index={index}
                      isCollapsed={Boolean(collapsedDays[key])}
                      onToggle={() => toggleDayCollapsed(day, index)}
                    />
                  );
                })}
              </motion.div>
            )
          )}

          {activeRunTab === "bookings" && (
            <BookingsTab
              trip={trip}
              automatedPayments={false}
              embedded
              selectedBatchId={selectedRunBatchId}
              onSelectedBatchIdChange={setSelectedRunBatchId}
              showBatchSelector={false}
            />
          )}

          {activeRunTab === "budget" && (
            <FinanceTab key={`${trip.id}:${selectedRunBatchId}`} trip={trip} batchId={selectedRunBatchId} embedded />
          )}
          {activeRunTab === "enquiries" && <EnquiriesTab trip={trip} batchId={selectedRunBatchId} />}

          {activeRunTab === "participants" && (
            <RunParticipantsTab
              trip={trip}
              canParticipantView={canParticipantView}
              canBookings={canBookings}
              canDocs={canDocs}
              participantSummary={participantSummary}
              documents={scopedDocuments}
              documentsError={participantDocuments.error}
              locationShares={locationShares.data}
              locationSharesError={locationShares.error}
              onOpenParticipants={() => setManagementPanel("participants")}
              onOpenMessages={(participant) => {
                setDirectMessageParticipant(participant);
                setRunTab("messages");
              }}
              onRefresh={load}
            />
          )}
        </div>
        {canCommunicate && (
          <Dialog open={communicationsOpen} onOpenChange={setCommunicationsOpen}>
            <div
              data-testid="run-communications-dock"
              className="pointer-events-none sticky bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 flex justify-end px-4 py-4 sm:px-5"
            >
              <DialogTrigger asChild>
                <button
                  type="button"
                  data-testid="run-communications-chip"
                  className="tp-run-communicate-chip pointer-events-auto inline-flex min-h-11 max-w-full items-center gap-2 rounded-full border border-[#E5B85C]/40 bg-[#17211D] px-4 py-3 text-sm font-bold text-white shadow-lg hover:bg-[#2D7A65] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2"
                >
                  <Send className="h-4 w-4 shrink-0" aria-hidden="true" /> Communications
                </button>
              </DialogTrigger>
            </div>
            <DialogContent
              aria-modal="true"
              aria-describedby={undefined}
              className="max-h-[calc(100dvh-2rem)] w-[calc(100%-1.5rem)] max-w-5xl overflow-y-auto rounded-lg border-[#E2DFD7] bg-[#F7F6F2] p-4 text-[#17211D] sm:p-5"
            >
              <DialogTitle className="pr-8 text-base font-black">Communications</DialogTitle>
              <CommunicationsTab trip={trip} canEdit={canCommunicate} batchId={selectedRunBatchId} />
            </DialogContent>
          </Dialog>
        )}
      </motion.section>

      <AnimatePresence>
        {managementPanel === "participants" && (
          <ManagementModal
            key="participants"
            title="Participants"
            subtitle="Invite interested travellers, track accepted participants, and remove people from this trip."
            onClose={() => { setManagementPanel(""); load(); }}
          >
            <ParticipantsTab trip={trip} canEdit={canEdit} batchId={selectedRunBatchId} />
          </ManagementModal>
        )}

      </AnimatePresence>
    </motion.div>
  );
}
